///
/// EarthBrowser
///
/// Legend card shell, its collapsed chip, and the tray's show/hide toggle.
/// Every layer legend renders through these so the header and collapse
/// chevron look and behave the same; the body comes from the layer's
/// LegendDefinition.
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///

import { LoadRing } from "@/components/ui/load-ring"
import type { DisplayedLoadState } from "@/hooks/useLayerLoadStates"
import { cn } from "@/utils/utils"
import { ChevronDown, SwatchBook } from "lucide-react"
import type { ReactNode } from "react"
import type { LegendAccent } from "./legendTypes"

// Literal class names so Tailwind sees them
const cAccentClasses: Record<LegendAccent, { text: string; ring: string }> = {
	orange: { text: "text-orange-400", ring: "focus-visible:ring-orange-500/60" },
	violet: { text: "text-violet-300", ring: "focus-visible:ring-violet-500/60" },
	rose: { text: "text-rose-400", ring: "focus-visible:ring-rose-500/60" },
	emerald: { text: "text-emerald-400", ring: "focus-visible:ring-emerald-500/60" },
	sky: { text: "text-sky-400", ring: "focus-visible:ring-sky-500/60" },
	red: { text: "text-red-400", ring: "focus-visible:ring-red-500/60" },
	cyan: { text: "text-cyan-400", ring: "focus-visible:ring-cyan-500/60" },
	amber: { text: "text-amber-400", ring: "focus-visible:ring-amber-500/60" },
}

// Same glass family as the time control and camera stack, with a darker
// fill so fine legend text and swatches stay legible over a bright globe
const cSurface = "bg-black/60 backdrop-blur-xl border border-white/20 shadow-2xl"

// Layer icons come in at h-4; the card header and chip run them at 3.5.
// Relative so the loading ring can wrap the icon
const cIconSlot = "relative flex-shrink-0 flex items-center justify-center h-4 w-4 [&>svg:not([data-load-ring])]:h-3.5 [&>svg:not([data-load-ring])]:w-3.5"

/** Icon with its loading ring; the one place the tray says "data still coming" with the panel closed */
function IconSlot({ icon, loadState }: { icon: ReactNode; loadState?: DisplayedLoadState | null }) {
	return (
		<span className={cIconSlot}>
			{icon}
			<LoadRing state={loadState ?? null} strokeWidth={2.5} />
		</span>
	)
}

interface LegendCardProps {
	title: string
	icon: ReactNode
	accent: LegendAccent
	/** Data still coming for the layer; rings the header icon */
	loadState?: DisplayedLoadState | null
	/** Status text for the ring's tooltip */
	loadingLabel?: string
	onCollapse: () => void
	children: ReactNode
}

export function LegendCard({ title, icon, accent, loadState, loadingLabel, onCollapse, children }: LegendCardProps) {
	const colors = cAccentClasses[accent]
	const loading = loadState && loadingLabel && !loadState.done
	return (
		<div
			className={cn("pointer-events-auto w-56 rounded-xl text-slate-200", cSurface)}
			role="region"
			aria-label={`${title} legend`}
		>
			<button
				onClick={onCollapse}
				className={cn(
					"w-full flex items-center gap-1.5 px-3 py-2 rounded-t-xl",
					"text-xs font-semibold uppercase tracking-wider",
					"hover:brightness-125 transition-[filter]",
					"focus-visible:outline-none focus-visible:ring-2",
					colors.text, colors.ring
				)}
				aria-label={loading ? `Collapse ${title} legend (${loadingLabel})` : `Collapse ${title} legend`}
				aria-expanded="true"
				// The header is too narrow for the status beside the title; the
				// ring carries it and the tooltip spells it out
				title={loading ? loadingLabel : "Collapse legend to a chip"}
			>
				<IconSlot icon={icon} loadState={loadState} />
				<span className="truncate">{title}</span>
				<ChevronDown className="h-3.5 w-3.5 ml-auto flex-shrink-0" />
			</button>
			<div className="px-3 pb-3 space-y-2 text-xs">
				{children}
			</div>
		</div>
	)
}

const cChipClasses = cn(
	"pointer-events-auto flex items-center gap-1.5 px-2.5 py-1.5 rounded-full",
	"text-xs text-slate-200 hover:bg-black/80 hover:text-white transition-colors",
	"focus-visible:outline-none focus-visible:ring-2",
	cSurface
)

interface LegendChipProps {
	title: string
	label: string
	icon: ReactNode
	accent: LegendAccent
	/** Data still coming for the layer; rings the chip icon */
	loadState?: DisplayedLoadState | null
	/** Status text for the ring's tooltip and accessible name */
	loadingLabel?: string
	onExpand: () => void
}

export function LegendChip({ title, label, icon, accent, loadState, loadingLabel, onExpand }: LegendChipProps) {
	const loading = loadState && loadingLabel && !loadState.done
	return (
		<button
			onClick={onExpand}
			className={cn(cChipClasses, cAccentClasses[accent].ring)}
			aria-label={loading ? `Show ${title} legend (${loadingLabel})` : `Show ${title} legend`}
			aria-expanded="false"
			title={loading ? loadingLabel : undefined}
		>
			<IconSlot icon={icon} loadState={loadState} />
			<span>{label}</span>
		</button>
	)
}

/**
 * The tray's one on/off control: a chip-height round button that is always
 * present while any legend layer is visible, lit while legends show. The
 * fixed anchor is what makes hiding safe: the way back never moves
 */
export function LegendTrayToggle({ shown, onToggle }: { shown: boolean; onToggle: () => void }) {
	return (
		<button
			onClick={onToggle}
			className={cn(
				"pointer-events-auto flex items-center justify-center h-[30px] w-[30px] rounded-full",
				"transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40",
				cSurface,
				shown ? "text-white hover:bg-black/80" : "text-slate-400 hover:text-white hover:bg-black/80"
			)}
			aria-label={shown ? "Hide legends" : "Show legends"}
			aria-pressed={shown}
			title={shown ? "Hide legends" : "Show legends"}
		>
			<SwatchBook className="h-3.5 w-3.5" />
		</button>
	)
}
